import { Trans } from "@lingui/react/macro";
import React from "react";
import {
  Button,
  FareInfographicsMosaic,
  InclusionContent
} from "@silversea-ssc/ui-components-react";
import type { InclusionCode } from "@silversea-ssc/web-inclusions";
import {
  CancellationPolicyModalContentFragment,
  ExpeditionInclusionModalContentFragment,
  FareMosaicCardFragment,
  GenericInfographicStepInclusionFragment,
  InfographicCardInclusionContentFragment
} from "types/graphql-types";
import { IconsId } from "@silversea-ssc/design-system-icons";
import FareCard, { FareCardProps } from "../FareCard/FareCard";
import { InclusionsDrawerProvider } from "components/fare/InclusionsDrawer/InclusionsDrawerProvider";
import { extractFareCardHeaderContent } from "components/fare/FareCard/utils";
import { FareCode } from "@silversea-ssc/web-fares";
import { ExpandableFareCardProvider } from "../ExpandableFareCardProvider";
import { FarePenaltiesModalProvider } from "components/FarePenaltiesModal/FarePenaltiesModalProvider";
import {
  reduceToPaymentTermsApiResponse,
  useRemotePaymentTermsPerFares
} from "src/ssc-api/useRemotePaymentTerms";
import ExpeditionBox from "../ExpeditionBox/ExpeditionBox";
import { filterExpeditionInclisionsCodes } from "../ExpeditionBox/utils";

interface FaresMosaicProps {
  fares: FareMosaicCardFragment[];
  inclusionsContent: (InfographicCardInclusionContentFragment &
    GenericInfographicStepInclusionFragment)[];
  cardVariant: FareCardProps["variant"];
  cancellationPolicyModalContent: CancellationPolicyModalContentFragment;
  expeditionInclusionModalContent?: ExpeditionInclusionModalContentFragment;
}

const toInfographicContent = (
  content: InfographicCardInclusionContentFragment
): InclusionContent => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    _id: content._id!,
    icon: content.icon as IconsId,
    codes: content.codes as InclusionCode[],
    infographicTitle: content.genericContent?.infographicTitle?.localized || "",
    title: content.genericContent?.title?.localized,
    description: content.genericContent?.description?.localized,
    footnote: content.genericContent?.footnote?.localized
  };
};

const StaticFaresMosaic: React.FC<
  React.PropsWithChildren<FaresMosaicProps>
> = ({
  fares,
  inclusionsContent,
  cardVariant,
  cancellationPolicyModalContent,
  expeditionInclusionModalContent
}) => {
  const remotePaymentTerms = useRemotePaymentTermsPerFares(
    fares.flatMap((f) => f.fareCode as FareCode)
  );

  return (
    <ExpandableFareCardProvider>
      <FarePenaltiesModalProvider
        content={cancellationPolicyModalContent}
        showCruiseGroupDisclaimer
        remotePaymentTerms={reduceToPaymentTermsApiResponse(remotePaymentTerms)}
      >
        <InclusionsDrawerProvider
          inclusionsContent={inclusionsContent.map(toInfographicContent)}
        >
          <div>
            <FareInfographicsMosaic>
              {fares.map((fare) => {
                const { enabledInclusions, _id } = fare;
                const fareCode = fare.fareCode as FareCode;

                return (
                  <FareCard
                    {...extractFareCardHeaderContent(fare)}
                    variant={cardVariant}
                    key={_id}
                    fareCode={fareCode}
                    enabledInclusions={(
                      enabledInclusions as InclusionCode[]
                    ).filter(filterExpeditionInclisionsCodes)}
                    servicesDisclaimer={fare.infographicFooterNotes?.localized}
                    footerNotesForNonAirCountries={
                      fare.infographicFooterNotesForNonAirCountries?.localized
                    }
                    ctaButton={
                      fare.fullPath ? (
                        <Button
                          variant="primary"
                          href={fare.fullPath}
                          iconName="arrow-right"
                          dataContentPiece="fare_details"
                        >
                          <Trans>More info and voyages</Trans>
                        </Button>
                      ) : undefined
                    }
                    //cruiseGroup={cruiseGroup}
                    //inclusionsContentTokens={f.tokenValues}
                  />
                );
              })}
            </FareInfographicsMosaic>
            {expeditionInclusionModalContent && (
              <ExpeditionBox content={expeditionInclusionModalContent} />
            )}
          </div>
        </InclusionsDrawerProvider>
      </FarePenaltiesModalProvider>
    </ExpandableFareCardProvider>
  );
};

export default StaticFaresMosaic;
